import { useMemo, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useClickOutside } from '../hooks/useClickOutside';
import { cn } from '../lib/cn';

export interface MultiSelectOption {
  value: string;
  label: string;
  group?: string; // bucket id for the optional "Show" segmented filter
}

export interface MultiSelectSegment {
  id: string;
  label: string;
}

// Searchable checkbox-list dropdown. Empty selection means "all" (the placeholder
// label); one selection shows its name; more show "N selected". Type to filter,
// Enter toggles the top match, Esc closes. When `segments` is provided, a "Show"
// segmented control filters the list by each option's `group` (the segment id
// 'all' shows everything); selections persist even when their option is hidden.
export function MultiSelect({
  placeholder,
  options,
  value,
  onChange,
  disabled,
  disabledLabel,
  segments,
  segment,
  onSegmentChange,
}: {
  placeholder: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (v: string[]) => void;
  disabled?: boolean;
  disabledLabel?: string;
  segments?: MultiSelectSegment[];
  segment?: string;
  onSegmentChange?: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const close = () => {
    setOpen(false);
    setQ('');
  };
  const ref = useClickOutside<HTMLDivElement>(close);

  const label = disabled
    ? (disabledLabel ?? placeholder)
    : value.length === 0
      ? placeholder
      : value.length === 1
        ? (options.find((o) => o.value === value[0])?.label ?? placeholder)
        : `${value.length} selected`;

  // Resolve the active segment once so filtering and the pill highlight agree
  // (an uncontrolled `segment` falls back to the first segment).
  const activeSeg = segments ? (segment ?? segments[0]?.id) : undefined;
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return options.filter(
      (o) =>
        // Always keep selected options visible so they can be unchecked even when
        // the current segment would otherwise hide them.
        (value.includes(o.value) || !activeSeg || activeSeg === 'all' || o.group === activeSeg) &&
        (!needle || o.label.toLowerCase().includes(needle)),
    );
  }, [options, q, activeSeg, value]);
  const hiddenBySegment = !!activeSeg && activeSeg !== 'all' && filtered.length === 0 && options.length > 0;

  const toggle = (v: string) =>
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') close();
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[0]) toggle(filtered[0].value);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-2 text-left text-sm',
          disabled ? 'cursor-not-allowed bg-slate-50 dark:bg-slate-800 text-slate-400' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700',
          !disabled && value.length > 0 && 'border-[#FF5A3C]/50 ring-1 ring-[#FF5A3C]/20',
        )}
      >
        <span className="truncate">{label}</span>
        <ChevronDown size={14} className="shrink-0 text-slate-400" />
      </button>
      {open && !disabled && (
        <div className="absolute left-0 right-0 top-full z-40 mt-1 min-w-[200px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg">
          <div className="relative border-b border-slate-100 dark:border-slate-700 p-1.5">
            <Search size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search…"
              className="w-full rounded-md border border-slate-200 dark:border-slate-700 py-1.5 pl-7 pr-2 text-sm placeholder:text-slate-400 focus:border-[#FF5A3C] focus:outline-none focus:ring-1 focus:ring-[#FF5A3C]/20"
            />
          </div>
          {segments && (
            <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-700 px-2.5 py-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Show</span>
              <div className="ml-auto flex gap-0.5">
                {segments.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => onSegmentChange?.(s.id)}
                    className={cn(
                      'rounded px-2 py-0.5 text-xs font-semibold',
                      activeSeg === s.id
                        ? 'bg-[#FF5A3C]/10 text-[#D8341A] dark:bg-[#FF5A3C]/20 dark:text-[#FF8A6B]'
                        : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700',
                    )}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div role="listbox" aria-multiselectable="true" className="max-h-56 overflow-y-auto py-1">
            {!q && (
              <button
                onClick={() => onChange([])}
                className="block w-full border-b border-slate-100 dark:border-slate-700 px-3 py-1.5 text-left text-xs font-semibold text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                {placeholder}
              </button>
            )}
            {filtered.map((o) => {
              const on = value.includes(o.value);
              return (
                <button
                  key={o.value}
                  role="option"
                  aria-selected={on}
                  onClick={() => toggle(o.value)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                  <span
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                      on ? 'border-[#FF5A3C] bg-[#FF5A3C] text-white' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800',
                    )}
                  >
                    {on && <Check size={11} strokeWidth={3} />}
                  </span>
                  <span className="truncate text-slate-700 dark:text-slate-200">{o.label}</span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-center text-xs text-slate-400">
                {q
                  ? `No matches for “${q}”.`
                  : hiddenBySegment
                    ? 'None in this view — try a different Show filter.'
                    : 'No options.'}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
